import { useEffect, useState } from "react";
import UseContext from "hooks/useContext";

// The launcher is injected by the Intercom script, outside React: the lightweight one before the
// messenger boots, then the iframe of the booted messenger.
const LAUNCHER_SELECTOR = ".intercom-lightweight-app-launcher, .intercom-launcher-frame, .intercom-launcher";

/** Space between the launcher and the tooltip, as the `space` of components/tooltips. */
const GAP = 12;

/**
 * "Besoin d'aide ?" on hover of the Intercom launcher, on its left. The launcher cannot be wrapped
 * by components/tooltips (it is not a React element), so this one follows it in the DOM and draws
 * the same tooltip: #1F2937, 14px text, 8px x 16px padding, 4px corners.
 */
const IntercomLauncherTooltip = () => {
    const { language } = UseContext() ?? {};
    const [position, setPosition] = useState(null);

    useEffect(() => {
        let launcher = null;
        const show = () => {
            const rect = launcher?.getBoundingClientRect();
            if (rect) setPosition({ right: window.innerWidth - rect.left + GAP, top: rect.top + rect.height / 2 });
        };
        const hide = () => setPosition(null);

        const attach = () => {
            const found = document.querySelector(LAUNCHER_SELECTOR);
            if (found === launcher) return;
            launcher?.removeEventListener("mouseenter", show);
            launcher?.removeEventListener("mouseleave", hide);
            launcher = found;
            launcher?.addEventListener("mouseenter", show);
            launcher?.addEventListener("mouseleave", hide);
            if (!launcher) hide();
        };

        attach();
        // The launcher appears late and is replaced once the messenger boots.
        const observer = new MutationObserver(attach);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => {
            observer.disconnect();
            launcher?.removeEventListener("mouseenter", show);
            launcher?.removeEventListener("mouseleave", hide);
        };
    }, []);

    if (!position) return null;

    return (
        <span
            role="tooltip"
            className="pointer-events-none fixed z-[2147483001] -translate-y-1/2 whitespace-nowrap rounded bg-[#1F2937] px-4 py-2 text-sm font-normal tracking-[0.02em] text-white"
            style={{ right: position.right, top: position.top }}
        >
            {language?.getValue("intercom.launcherTooltip", "Besoin d'aide ?") ?? "Besoin d'aide ?"}
        </span>
    );
};

export default IntercomLauncherTooltip;
